import { useMemo, useState } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
    Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
    ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

const fmtUSD = (v) =>
    v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function project({ initial, yieldPct, dgrPct, years, reinvest, annualContribution }) {
    const rows = [];
    let capital = Number(initial) || 0;
    let dpsYieldOnCost = (Number(yieldPct) || 0) / 100;
    let totalIncome = 0;
    for (let y = 1; y <= years; y++) {
        const incomeThisYear = capital * dpsYieldOnCost;
        totalIncome += incomeThisYear;
        rows.push({
            year: y,
            income: Math.round(incomeThisYear),
            capital: Math.round(capital),
            yoc: +(dpsYieldOnCost * 100).toFixed(2),
            cumulative: Math.round(totalIncome),
        });
        // For next year:
        if (reinvest) capital += incomeThisYear;
        capital += Number(annualContribution) || 0;
        dpsYieldOnCost *= 1 + (Number(dgrPct) || 0) / 100;
    }
    return rows;
}

export default function Calculator() {
    const [form, setForm] = useState({
        initial: 25000,
        yieldPct: 3.5,
        dgrPct: 7,
        years: 25,
        reinvest: true,
        annualContribution: 2400,
    });

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const rows = useMemo(() => project(form), [form]);
    const last = rows[rows.length - 1] || { income: 0, capital: 0, yoc: 0, cumulative: 0 };
    const startingIncome = rows[0]?.income || 0;
    const incomeGrowth = startingIncome ? (last.income / startingIncome - 1) * 100 : 0;

    return (
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
            <div className="mb-8 animate-fade-up">
                <div className="overline">Tools</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
                    Dividend income calculator
                </h1>
                <p className="mt-2 text-muted-foreground max-w-2xl">
                    Project annual dividend income, yield-on-cost and total income from a starting position,
                    optional contributions and dividend reinvestment.
                </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* INPUTS */}
                <div className="rounded-2xl border border-border bg-background p-6" data-testid="calc-inputs">
                    <div className="overline">Assumptions</div>
                    <div className="font-heading text-lg font-semibold mt-1 mb-5">Your scenario</div>

                    <div className="space-y-4">
                        <Field label="Initial capital" unit="$">
                            <Input type="number" min="0" value={form.initial}
                                onChange={(e) => set("initial", e.target.value)}
                                data-testid="calc-initial" />
                        </Field>
                        <Field label="Annual contribution" unit="$">
                            <Input type="number" min="0" value={form.annualContribution}
                                onChange={(e) => set("annualContribution", e.target.value)}
                                data-testid="calc-contribution" />
                        </Field>
                        <Field label="Starting yield" unit="%">
                            <Input type="number" step="0.1" min="0" value={form.yieldPct}
                                onChange={(e) => set("yieldPct", e.target.value)}
                                data-testid="calc-yield" />
                        </Field>
                        <Field label="Annual dividend growth (DGR)" unit="%">
                            <Input type="number" step="0.1" min="0" value={form.dgrPct}
                                onChange={(e) => set("dgrPct", e.target.value)}
                                data-testid="calc-dgr" />
                        </Field>
                        <Field label="Years" unit="yr">
                            <Input type="number" min="1" max="60" value={form.years}
                                onChange={(e) => set("years", e.target.value)}
                                data-testid="calc-years" />
                        </Field>

                        <div className="flex items-center justify-between pt-3 border-t border-border">
                            <Label htmlFor="reinvest" className="text-sm">Reinvest dividends (DRIP)</Label>
                            <Switch id="reinvest"
                                checked={form.reinvest}
                                onCheckedChange={(v) => set("reinvest", v)}
                                data-testid="calc-reinvest" />
                        </div>
                    </div>
                </div>

                {/* RESULTS */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KPI label={`Income in year ${form.years}`} value={fmtUSD(last.income)} primary />
                        <KPI label="Capital at end" value={fmtUSD(last.capital)} />
                        <KPI label="Yield on cost" value={`${last.yoc.toFixed(2)}%`} />
                        <KPI label="Total income paid" value={fmtUSD(last.cumulative)} />
                    </div>

                    <div className="rounded-2xl border border-border bg-background p-6">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="overline">Projection</div>
                                <div className="font-heading text-lg font-semibold mt-1">Annual dividend income</div>
                            </div>
                            <div className="text-xs text-muted-foreground tabular-nums">
                                +{incomeGrowth.toFixed(0)}% over {form.years}y
                            </div>
                        </div>
                        <div className="h-72 min-h-[280px]" data-testid="calc-chart">
                            <ResponsiveContainer width="100%" height="100%" minHeight={280}>
                                <BarChart data={rows} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                                    <CartesianGrid stroke="#E5E7EB" strokeDasharray="2 4" vertical={false} />
                                    <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false}
                                        tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 12 }}
                                        formatter={(v, n) => [fmtUSD(v), n === "income" ? "Annual income" : n]}
                                        labelFormatter={(y) => `Year ${y}`}
                                    />
                                    <Bar dataKey="income" radius={[6, 6, 0, 0]}>
                                        {rows.map((_, i) => <Cell key={i} fill={i === rows.length - 1 ? "#047857" : "#059669"} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-border bg-background p-6">
                        <div className="overline">Schedule</div>
                        <div className="font-heading text-lg font-semibold mt-1 mb-4">Year-by-year</div>
                        <div className="overflow-auto max-h-72 border border-border rounded-xl" data-testid="calc-table">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/40 text-xs text-muted-foreground sticky top-0">
                                    <tr>
                                        <th className="text-left px-3 py-2 font-semibold">Year</th>
                                        <th className="text-right px-3 py-2 font-semibold">Capital</th>
                                        <th className="text-right px-3 py-2 font-semibold">YoC</th>
                                        <th className="text-right px-3 py-2 font-semibold">Income</th>
                                        <th className="text-right px-3 py-2 font-semibold">Cumulative</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.year} className="border-b border-border last:border-0">
                                            <td className="px-3 py-2 font-medium">{r.year}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{fmtUSD(r.capital)}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{r.yoc.toFixed(2)}%</td>
                                            <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">{fmtUSD(r.income)}</td>
                                            <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmtUSD(r.cumulative)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, unit, children }) {
    return (
        <div>
            <div className="flex items-baseline justify-between">
                <Label className="text-xs">{label}</Label>
                <span className="text-[10px] text-muted-foreground">{unit}</span>
            </div>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}

function KPI({ label, value, primary }) {
    return (
        <div className={`rounded-xl border border-border p-4 ${primary ? "bg-accent" : "bg-background"}`}>
            <div className="overline">{label}</div>
            <div className="mt-2 font-heading text-xl font-bold tabular-nums">{value}</div>
        </div>
    );
}
